import { useTranslation } from 'module/hooks/use-translation';
import { GroupedFilter } from 'module/types/filter-field/grouped-filter/filter';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { SearchInputExpanded } from 'module/ui/components/atoms/input/search-input-expanded';
import { Loading } from 'module/ui/components/atoms/loading';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import moment from 'moment';
import { useGetAllEmailsQueryWithScrollPagination } from 'queries/emails';
import { useCallback, useMemo, useState } from 'react';
import { FixedSizeList as List } from 'react-window';
import styled from 'styled-components';

const RowWrapper = styled.div`
  height: 100%;
  box-sizing: border-box;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 400px;
  border-radius: 8px;
  overflow: hidden;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  height: 32px;
  margin: 16px 16px 8px;
  background: var(--white-100);
  border-radius: 8px;
`;

const SearchInput = styled(SearchInputExpanded)`
  width: 100%;
  height: 100%;
  border: none;
  font-family: var(--font-family);
  font-size: 14px;
  line-height: 1.286;
`;

const EmailListContainer = styled.div<{ isLoading: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  justify-content: ${(props) => (props.isLoading ? 'center' : 'flex-start')};
`;

const MenuItem = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 200px;
  width: calc(100% - 2 * var(--spacing-03));
  margin: 0 var(--spacing-03);
  height: 100%;
  box-sizing: border-box;
  padding: 0 var(--spacing-05);
  background: ${(props) =>
    props.isSelected ? 'var(--blue-20)' : 'transparent'};
  border-bottom: 1px solid var(--divider-default);
  border-radius: var(--border-radius-default);
  cursor: pointer;

  &:hover {
    background: ${(props) =>
      props.isSelected ? 'var(--blue-20)' : 'var(--scarlet-gray-10)'};
  }
`;

const EmailContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--spacing-03);
  flex: 1;
  min-width: 0;
  padding-right: var(--spacing-03);
`;

const EmailName = styled.div`
  font-family: var(--font-family);
  font-size: 14px;
  line-height: 1.286;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmailDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-family);
  font-size: 12px;
  line-height: 1.333;
`;

const EmailTime = styled.span`
  color: var(--text-secondary);
  white-space: nowrap;
`;

const Dot = styled.span`
  width: 4px;
  height: 4px;
  background-color: var(--text-secondary);
  border-radius: 50%;
  flex-shrink: 0;
`;

const EmailSender = styled.span`
  color: var(--text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CheckIcon = styled(FontAwesomeIcon)<{ $visible: boolean }>`
  width: 16px;
  height: 16px;
  color: var(--text-primary);
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  flex-shrink: 0;
`;

const LoadingItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 65px;
  color: var(--text-secondary);
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 52px;
  color: var(--text-tertiary);
  gap: var(--spacing-03);
`;

const DEFAULT_LIST_WIDTH = 700;

export const EmailList = ({
  emails: externalEmails,
  isLoading: externalIsLoading,
  isDropdownOpen,
  selectedEmailId,
  handleMenuClick,
  filter,
  height = 350,
  showSearch = false,
  listWidth = DEFAULT_LIST_WIDTH,
  /** When true, paginated fetch runs whenever this component is mounted (e.g. section preview with list). Otherwise fetch runs only while the dropdown is open. */
  alwaysEnablePagination = false,
}: {
  emails?: any;
  isLoading: boolean;
  selectedEmailId?: number;
  isDropdownOpen?: boolean;
  handleMenuClick: (emailId: number, email: any) => void;
  filter?: GroupedFilter;
  height?: number;
  showSearch?: boolean;
  listWidth?: number;
  alwaysEnablePagination?: boolean;
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Translation strings
  const searchPlaceholder = useTranslation('email_list.search_placeholder');
  const loadingMoreText = useTranslation('email_list.loading_more');
  const noMoreEmailsText = useTranslation('email_list.no_more');
  const unknownSenderText = useTranslation('email_list.unknown_sender');
  const loadingText = useTranslation('email_list.loading');
  const {
    emails: paginatedEmails,
    isLoading: isPaginatedLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetAllEmailsQueryWithScrollPagination(filter, {
    enabled: alwaysEnablePagination || Boolean(isDropdownOpen),
    limit: 25,
  });

  const emailList = useMemo(() => {
    const allEmails = externalEmails || paginatedEmails || [];

    if (!searchQuery.trim()) {
      return allEmails;
    }

    return allEmails.filter(
      (email) =>
        email.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        email.sender?.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [externalEmails, paginatedEmails, searchQuery]);

  const isLoading = externalIsLoading || isPaginatedLoading;

  // Handle scroll to load more data when near bottom
  const handleItemsRendered = useCallback(
    ({ visibleStopIndex }: { visibleStopIndex: number }) => {
      const threshold = 5; // Load more when 5 items from bottom

      if (
        hasNextPage &&
        !isFetchingNextPage &&
        visibleStopIndex >= emailList.length - threshold
      ) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage, emailList.length],
  );

  // Calculate total item count (current items + 1 if more to load)
  const itemCount = hasNextPage ? emailList.length + 1 : emailList.length;

  const Row = ({ index, style }) => {
    const email = emailList[index];

    // Show loading item if we're at the end and fetching more
    if (!email) {
      return (
        <RowWrapper style={style}>
          <LoadingItem>
            {isFetchingNextPage ? (
              <>
                <Loading size="small" />
                <div>{loadingMoreText}</div>
              </>
            ) : (
              <div>{noMoreEmailsText}</div>
            )}
          </LoadingItem>
        </RowWrapper>
      );
    }

    const isSelected = selectedEmailId === email.emailThreadId;

    return (
      <RowWrapper style={style}>
        <MenuItem
          isSelected={isSelected}
          onClick={() => handleMenuClick(email.emailThreadId, email)}
        >
          <EmailContent>
            <EmailName>{email.subject}</EmailName>
            <EmailDetails>
              <EmailTime>
                {moment(email.updatedAt).format('D MMM h:mmA')}
              </EmailTime>
              <Dot />
              <EmailSender>{email.sender || unknownSenderText}</EmailSender>
            </EmailDetails>
          </EmailContent>
          <CheckIcon icon={faCheck} $visible={isSelected} />
        </MenuItem>
      </RowWrapper>
    );
  };

  return (
    <Container>
      {showSearch && (
        <SearchContainer>
          <SearchInput
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery('')}
          />
        </SearchContainer>
      )}
      <EmailListContainer isLoading={isLoading && emailList.length === 0}>
        {isLoading && emailList.length === 0 ? (
          <LoadingState>
            <Loading size="default" />
            <div>{loadingText}</div>
          </LoadingState>
        ) : (
          <List
            height={height}
            itemCount={itemCount}
            itemSize={65}
            width={listWidth}
            onItemsRendered={handleItemsRendered}
          >
            {Row}
          </List>
        )}
      </EmailListContainer>
    </Container>
  );
};
